import { observer } from 'mobx-react';
import momentTimezone from 'moment-timezone';
import qs from 'qs';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { Container } from 'reactstrap';
import { from, fromEvent } from 'rxjs';
import { debounceTime, share } from 'rxjs/operators';
import { ThemeContext } from '../../apiContexts';
import InputSearchWithRef from '../../components/inputSearch/inputSearchWithRef';
import LoadingBar from '../../components/loadingBar';
import ReactTable from '../../components/reactTable';
import ReactExport from 'react-export-excel';
import RoundButton from '../../components/roundButton';
import { communicationLabel, activeLabel, communicationTime, createLabelIcon, getCurrentCategories, getDateGroup, getGroupByCategoryStatus, getDeviceActiveGroup, getStatusGroup, statusLabel } from '../../helpers/admin';
import { getOffsetTo } from '../../helpers/DOM';
import { getColumnWidth } from '../../helpers/report';
import useStore from '../../hooks/use-store';
import history from '../../routes/history';
import { ReactSelectStyled } from '../../styles';
import { Header, ListStyle } from '../../views/reports/styles';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const activeOptions = activeLabel.map((l) => ({
  id: l,
  title: l,
}));

const communicationOptions = communicationLabel.map((l, i) => ({
  id: communicationTime[i] || 'never',
  title: l,
}));

const stateOptions = statusLabel.map((l) => ({
  id: l === 'Desconhecido' ? 'unknown' : l,
  title: l,
}));

const typeOptions = [
  { id: 'device', title: 'Dispositivos Cadastrados' },
  { id: 'communication', title: 'Última Comunicação' },
  { id: 'state', title: 'Status de Conexão' },
  { id: 'status', title: 'Conexão dos Dispositivos' },
];

let categoryOptions;

const AdminDetailChartContainer = ({ location, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState();
  const [options, setOptions] = useState([]);

  const [typeOption, setTypeOption] = useState();
  const [statusOption, setStatusOption] = useState();

  const [offset, setOffset] = useState();

  const theme = useContext(ThemeContext);

  const { type } = useParams();

  const intl = useIntl();

  const query = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        comma: true,
      }),
    [location.search],
  );

  const { store } = useStore();
  const { server, session, devices } = store;

  const timezone = useMemo(
    () =>
      session.attributes.hasOwnProperty('timezone') ?
        session.attributes.timezone :
        server.attributes.timezone,
    [],
  );

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'category',
        Cell: ({ cell: { value } }) => createLabelIcon(value),
        width: 60,
      },
      {
        Header: 'Nome',
        accessor: 'name',
        minWidth: 200,
        width: getColumnWidth(data, 'name', 'Nome'),
      },
      {
        Header: 'Data do servidor',
        accessor: 'lastUpdate',
        Cell: ({ cell: { value } }) =>
          !value ?
            'D/N' :
            momentTimezone(value)
              .tz(timezone)
              .format('DD/MM/YYYY [-] HH:mm:ss'),
        minWidth: 200,
      },
      {
        Header: 'Data da posição',
        accessor: (d) => d.currentPosition,
        Cell: ({ cell: { value } }) =>
          !value ?
            'D/N' :
            momentTimezone(value.deviceTime)
              .tz(timezone)
              .format('DD/MM/YYYY [-] HH:mm:ss'),
        minWidth: 200,
      },
      {
        Header: 'Endereço',
        accessor: (d) => d.currentPosition,
        Cell: ({ cell: { value } }) => (value ? value.address || '-' : '-'),
        minWidth: 150,
        width: getColumnWidth(
          data,
          ({ currentPosition }) =>
            currentPosition ? currentPosition.address : '',
          'Endereço',
        ),
      },
    ],
    [data],
  );

  const inputRef = useRef();

  useEffect(() => {
    fromEvent(inputRef.current, 'input')
      .pipe(debounceTime(500))
      .subscribe((value) => {
        value = value.target.value;
        if (!value) setFiltered(null);
        else {
          setFiltered(
            data.filter(({ name }) =>
              String(name)
                .toUpperCase()
                .includes(String(value).toUpperCase()),
            ),
          );
        }
      });
  }, [data]);

  useEffect(() => {
    setLoading(true);
    if (devices.updatedPositions) {
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [devices.updatedPositions]);

  const headerTitle = useMemo(() => {
    if (type) {
      switch (type) {
        case 'communication':
          return 'Última Comunicação';
        case 'state':
          return 'Conexão dos Dispositivos';
        case 'device':
          return 'Dispositivos Cadastrados';
        default:
          return 'Status de Conexão';
      }
    }
  }, [type]);

  const sortByLastUpdate = (data) => {
    return data.sort(function (a, b) {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return new Date(b.lastUpdate) - new Date(a.lastUpdate);
    });
  };

  useEffect(() => {
    if (statusOption && devices.list.length) {
      const source$ = from(devices.list).pipe(share());
      setLoading(true);
      switch (type) {
        case 'device':
          getDeviceActiveGroup(source$).subscribe((dateGroup) => {
            const data = sortByLastUpdate(dateGroup[String(statusOption.id).toLowerCase()]);
            setData(data);
            setLoading(false);
          });
          break;
        case 'communication':
          getDateGroup(source$, communicationTime).subscribe((dateGroup) => {
            const data = sortByLastUpdate(dateGroup[statusOption.id]);
            setData(data);
            setLoading(false);
          });
          break;
        case 'state':
          getStatusGroup(source$).subscribe((dateGroup) => {
            const data = sortByLastUpdate(dateGroup[String(statusOption.id).toLowerCase()]);
            setData(data);
            setLoading(false);
          });
          break;
        case 'status':
          const [category, status] = statusOption.id.split('-');
          getGroupByCategoryStatus(source$, category, status).subscribe(
            (dateGroup) => {
              setData(sortByLastUpdate(dateGroup));
              setLoading(false);
            },
          );
          break;
        default:
          break;
      }
    }
  }, [statusOption, devices.list, devices.updatedPositions]);

  useEffect(() => {
    if (type && devices.list.length) {
      setTypeOption(typeOptions.find((e) => e.id === type));

      let options;
      switch (type) {
        case 'device':
          options = activeOptions;
          break;
        case 'communication':
          options = communicationOptions;
          break;
        case 'state':
          options = stateOptions;
          break;
        case 'status':
          getCategories();
          options = categoryOptions;
          break;
        default:
          break;
      }

      setStatusOption(options.find((e) => e.id == query.group));
    }
  }, [type, devices.list]);

  useEffect(() => {
    if (typeOption && devices.list.length) {
      switch (typeOption.id) {
        case 'device':
          setOptions(activeOptions);
          break;
        case 'communication':
          setOptions(communicationOptions);
          break;
        case 'state':
          setOptions(stateOptions);
          break;
        case 'status':
          getCategories();
          setOptions(categoryOptions);
          break;
        default:
          break;
      }
    }
  }, [typeOption, devices.list]);

  const getCategories = useCallback(() => {
    if (!categoryOptions) {
      categoryOptions = getCurrentCategories(devices.list).reduce(
        (acc, cur) => [
          ...acc,
          ...statusLabel.map((l) => ({
            id: `${cur}-${l === 'Desconhecido' ? 'unknown' : l}`,
            title: `${intl.formatMessage({
              id: `category_${cur}`,
            })} - ${l}`,
          })),
        ],
        [],
      );
    }
  }, [devices.list]);

  const selectItem = ({ data }) => {
    if (['unknown', 'never'].includes(query.group)) return;
    history.push(`/admin/events/devices/${data.id}`);
  };

  const changeType = (data) => {
    setStatusOption(null);
    setTypeOption(data);
  };

  const changeOption = (data) => {
    setStatusOption(data);
    history.push(`/admin/chart/${typeOption.id}?group=${data.id}`);
  };

  const containerRef = useRef();
  const tableRef = useRef();

  useEffect(() => {
    setOffset(getOffsetTo(tableRef.current, containerRef.current));
  });

  const getlistHeight = useCallback(() => {
    if (!containerRef.current) return 0;
    return containerRef.current.clientHeight - 32 - offset;
  }, [tableRef.current]);

  return (
    <Container fluid className="px-4 pb-4 position-relative h-100">
      <ListStyle
        ref={containerRef}
        theme={theme}
        className="custom-shadow-2 position-relative w-100 h-100"
        style={{ bottom: 0 }}
      >
        <Header className="mb-4">
          <h5>{headerTitle}</h5>
          <RoundButton className="mt-3 mb-3" tag={Link} to={'/admin'}>
            <span className="fas fa-times text-secondary" />
          </RoundButton>
        </Header>

        <div className="d-flex">
          <ReactSelectStyled
            radius="9px"
            scoped={false}
            options={typeOptions}
            name="report"
            noOptionsMessage={() => 'Nenhum resultado encontrado!'}
            placeholder="Escolha um tipo"
            className="w-100 mb-3"
            onChange={changeType}
            value={typeOption}
          />

          <div className="px-2" />

          <ReactSelectStyled
            radius="9px"
            scoped={false}
            options={options}
            name="report"
            noOptionsMessage={() => 'Nenhum resultado encontrado!'}
            placeholder="Escolha um status"
            className="w-100 mb-3"
            onChange={changeOption}
            value={statusOption}
          />
        </div>

        <div className="row py-4">
          <div style={{ width: '90%', padding: 10 }}>
            <InputSearchWithRef
              placeholder="Filtre por dispositivos"
              inputRef={inputRef}
            />
          </div>
          <ExcelFile element={
            <RoundButton className="mt-3 mb-3 float-right" style={{ right: '-20px', top: '-12px', position: 'relative' }}>
              <span className="fas fa-download" />
            </RoundButton>
          }>
            <ExcelSheet data={filtered || data} name={`${intl.formatMessage({ id: 'deviceTitle' })}`}>
              <ExcelColumn label={`${intl.formatMessage({ id: 'sharedName' })}`} value="name" />
              <ExcelColumn label={`${intl.formatMessage({ id: 'sharedDate' })}`}
                value={(col) => !col.lastUpdate ?
                  'D/N' :
                  momentTimezone(col.lastUpdate)
                    .tz(timezone)
                    .format('DD/MM/YYYY [-] HH:mm:ss')} />
              <ExcelColumn label={`${intl.formatMessage({ id: 'positionAddress' })}`}
                value={(col) => col.currentPosition ? (col.currentPosition.address || '-') : '-'} />
            </ExcelSheet>
          </ExcelFile>
        </div>

        {loading ? (
          <LoadingBar />
        ) : (
            <div ref={tableRef}>
              <ReactTable
                data={filtered || data}
                columns={columns}
                selectItem={selectItem}
                listHeight={getlistHeight()}
              />
            </div>
          )}
      </ListStyle>
    </Container>
  );
};

export default observer(AdminDetailChartContainer);
